/**
 * @cfleet/auth - AuthProvider
 *
 * Provides authentication context for CFleet applications.
 * Uses Better Auth as the default authentication provider for all environments.
 * Local auth adapter is available for unit tests only.
 *
 * The provider uses an adapter pattern to abstract the authentication mechanism,
 * allowing seamless switching between auth providers without changing application code.
 *
 * Key Features:
 * - Multi-app support: Dispatcher, Driver, and Customer apps with different auth flows
 * - Pluggable auth adapters: BetterAuthAdapter (default, cookies + JWT extraction) or LocalAuthAdapter (tests only)
 * - Uses platform-agnostic storage abstraction (web localStorage / React Native SecureStore)
 * - Syncs tokens to @cfleet/api-client middleware for automatic request authentication
 * - For Better Auth: Extracts JWT from session_data cookie and syncs to api-client for Authorization header
 * - Supports multi-tenant scenarios with organization switching
 * - Hydrates auth state from cookies (better auth) or storage on mount (local auth)
 * - Normalizes user/driver/customer profiles to unified User type
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import {
  setTokens as setMiddlewareTokens,
  clearTokens as clearMiddlewareTokens,
  setTenantId as setMiddlewareTenantId,
} from '@cfleet/api-client';
import type {
  AuthContextValue,
  AuthTokens,
  User,
  LoginCredentials,
  LoginResponse,
  Organization,
  UserPermissions,
  UserRole,
  AppType,
  AppAuthConfig,
  AuthProviderType,
} from './types';
import { APP_AUTH_CONFIGS, DEFAULT_PERMISSIONS } from './types';
import { getTokenStorage, AUTH_STORAGE_KEYS } from './storage';
import { BetterAuthAdapter } from './adapters/better-auth';
import { LocalAuthAdapter } from './adapters/local-auth';
import type { AuthAdapter } from './adapters/types';
import { isTokenExpired } from './utils/token';

/** Default session lifetime in seconds — must match backend SESSION_EXPIRES_IN */
const DEFAULT_SESSION_EXPIRES_IN = 604800;

/** Hydration timeout in milliseconds */
const HYDRATION_TIMEOUT_MS = 5000;

const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthProviderProps {
  children: ReactNode;
  /** API base URL for authentication endpoints */
  apiBaseUrl?: string;
  /** App type - determines endpoints and response parsing (default: 'dispatcher') */
  appType?: AppType;
  /**
   * Auth provider type - determines which adapter to use
   * - 'better-auth': Cookie-based auth via Better Auth sidecar (default, for all environments)
   * - 'local': Direct API calls with bearer tokens (for unit tests only)
   */
  authProvider?: AuthProviderType;
  /**
   * Better Auth base URL (only used when authProvider='better-auth')
   * If not provided, derives from apiBaseUrl by removing '/api/v1' suffix
   */
  betterAuthUrl?: string;
  /** Custom auth configuration (overrides appType defaults) */
  authConfig?: Partial<AppAuthConfig>;
  /** Called when authentication state changes */
  onAuthStateChange?: (isAuthenticated: boolean) => void;
  /**
   * Custom auth endpoint paths (for better-auth compatibility)
   * @deprecated Use appType or authConfig instead
   */
  endpoints?: {
    login?: string;
    logout?: string;
    refresh?: string;
  };
}

/**
 * Normalize backend role to frontend UserRole type.
 * Backend returns lowercase roles (admin, manager, dispatcher, viewer, driver, customer),
 * frontend uses PascalCase (Admin, Manager, Dispatcher, Driver, Customer).
 *
 * Note: 'viewer' role from backend is mapped to 'Dispatcher' as per design decision,
 * since frontend doesn't have a separate Viewer role type.
 */
function normalizeRole(backendRole: string, appType: AppType): UserRole {
  const roleMap: Record<string, UserRole> = {
    admin: 'Admin',
    manager: 'Manager',
    dispatcher: 'Dispatcher',
    viewer: 'Dispatcher', // Map viewer to Dispatcher per design decision
    driver: 'Driver',
    customer: 'Customer',
    // Also support PascalCase input (passthrough)
    Admin: 'Admin',
    Manager: 'Manager',
    Dispatcher: 'Dispatcher',
    Driver: 'Driver',
    Customer: 'Customer',
  };

  const normalizedRole = roleMap[backendRole];
  if (normalizedRole) {
    return normalizedRole;
  }

  // Try case-insensitive lookup as fallback
  const lowerRole = backendRole.toLowerCase();
  const fallbackRole = roleMap[lowerRole];
  if (fallbackRole) {
    return fallbackRole;
  }

  // Default role based on app type if unknown role
  switch (appType) {
    case 'driver':
      return 'Driver';
    case 'customer':
      return 'Customer';
    default:
      return 'Dispatcher';
  }
}

/**
 * Normalize a profile from any app type to the unified User type.
 * This allows the same interface to be used across all apps.
 */
function normalizeProfile(profile: Record<string, unknown>, appType: AppType): User {
  // Extract common fields
  const id = (profile.id as string) || '';
  const name = (profile.name as string) || '';
  const email = (profile.email as string) || '';

  // Map role - normalize case from backend to frontend format
  let role: UserRole;
  if (profile.role && typeof profile.role === 'string') {
    role = normalizeRole(profile.role, appType);
  } else {
    // Default role based on app type
    switch (appType) {
      case 'driver':
        role = 'Driver';
        break;
      case 'customer':
        role = 'Customer';
        break;
      default:
        role = 'Dispatcher';
    }
  }

  // Handle avatar - driver uses avatar_url, others use avatar
  const avatar = (profile.avatar as string) || (profile.avatar_url as string) || undefined;

  // Handle organizations - driver has organization_ids array, dispatcher has full objects
  let organizations: Organization[] = [];
  if (profile.organizations && Array.isArray(profile.organizations)) {
    organizations = profile.organizations as Organization[];
  } else if (profile.organization_ids && Array.isArray(profile.organization_ids)) {
    // Convert organization_ids to Organization objects
    organizations = (profile.organization_ids as string[]).map((orgId) => ({
      id: orgId,
      name: orgId, // Name not available, use ID
      role: role,
    }));
  }

  // Build permissions - only dispatcher has detailed permissions
  const permissions: UserPermissions = (profile.permissions as UserPermissions) || {
    ...DEFAULT_PERMISSIONS,
  };

  return {
    id,
    name,
    email,
    role,
    avatar,
    permissions,
    organizations: organizations.length > 0 ? organizations : undefined,
    status: (profile.status as 'Active' | 'Inactive' | 'Suspended' | 'Pending') || undefined,
    created_at: (profile.created_at as string) || undefined,
    last_login: (profile.last_login as string) || undefined,
  };
}

/**
 * AuthProvider component
 *
 * Wraps the application to provide authentication context.
 * Handles token management, user state, and multi-tenant organization selection.
 * Uses Better Auth by default for all environments.
 *
 * @example
 * ```tsx
 * // Dispatcher app with Better Auth (default)
 * import { AuthProvider } from '@cfleet/auth';
 *
 * function App() {
 *   return (
 *     <AuthProvider
 *       apiBaseUrl="http://localhost:8081/api/v1"
 *       betterAuthUrl="http://localhost:8080"
 *     >
 *       <MyApp />
 *     </AuthProvider>
 *   );
 * }
 *
 * // Driver app with Better Auth
 * function DriverApp() {
 *   return (
 *     <AuthProvider
 *       apiBaseUrl="http://localhost:8081/api/v1"
 *       betterAuthUrl="http://localhost:8080"
 *       appType="driver"
 *     >
 *       <MyDriverApp />
 *     </AuthProvider>
 *   );
 * }
 *
 * // Unit tests with local auth (mocked)
 * function TestApp() {
 *   return (
 *     <AuthProvider
 *       apiBaseUrl="http://localhost:8081/api/v1"
 *       authProvider="local"
 *     >
 *       <MyApp />
 *     </AuthProvider>
 *   );
 * }
 * ```
 */
export function AuthProvider({
  children,
  apiBaseUrl = 'http://localhost:8081/api/v1',
  appType = 'dispatcher',
  authProvider = 'better-auth',
  betterAuthUrl,
  authConfig,
  onAuthStateChange,
  endpoints = {},
}: AuthProviderProps) {
  // Resolve auth configuration - merge custom config with app defaults
  const resolvedConfig = useMemo((): AppAuthConfig => {
    const baseConfig = APP_AUTH_CONFIGS[appType];

    // Apply custom endpoint overrides (legacy prop)
    const endpointOverrides = {
      login: endpoints.login || authConfig?.endpoints?.login || baseConfig.endpoints.login,
      logout: endpoints.logout || authConfig?.endpoints?.logout || baseConfig.endpoints.logout,
      refresh: endpoints.refresh || authConfig?.endpoints?.refresh || baseConfig.endpoints.refresh,
      session: authConfig?.endpoints?.session || baseConfig.endpoints.session,
    };

    return {
      ...baseConfig,
      ...authConfig,
      endpoints: endpointOverrides,
    };
  }, [appType, authConfig, endpoints]);

  // Create the appropriate auth adapter based on authProvider prop
  const adapter = useMemo((): AuthAdapter => {
    if (authProvider === 'better-auth') {
      // Better Auth adapter - uses cookies
      // Pass appType to use app-specific endpoints (e.g., /api/v1/customer/auth/* for customer app)
      const url = betterAuthUrl || apiBaseUrl.replace('/api/v1', '').replace('/api', '');
      return new BetterAuthAdapter(url, appType);
    }
    // Local auth adapter - uses bearer tokens
    return new LocalAuthAdapter(apiBaseUrl, resolvedConfig);
  }, [authProvider, betterAuthUrl, apiBaseUrl, resolvedConfig, appType]);

  const [user, setUser] = useState<User | null>(null);
  const [tokens, setTokensState] = useState<AuthTokens | null>(null);
  const [currentOrganization, setCurrentOrganizationState] = useState<Organization | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(true); // Start loading until hydration completes
  const [error, setError] = useState<string | null>(null);
  const [isSessionInvalidating, setIsSessionInvalidating] = useState(false);

  // Track if hydration has been attempted
  const hydrationAttempted = useRef(false);

  const isAuthenticated = useMemo(() => !!tokens && !!user, [tokens, user]);

  // Notify on auth state changes
  useEffect(() => {
    onAuthStateChange?.(isAuthenticated);
  }, [isAuthenticated, onAuthStateChange]);

  /**
   * Persist tokens and user to storage, and sync to api-client middleware
   */
  const persistAndSyncTokens = useCallback(
    async (newTokens: AuthTokens, newUser: User, org?: Organization | null) => {
      const storage = getTokenStorage();

      // 1. Update React state
      setTokensState(newTokens);
      setUser(newUser);
      if (org !== undefined) {
        setCurrentOrganizationState(org);
      }

      // 2. Sync tokens to api-client middleware (for automatic Authorization header)
      // Only set if we have actual tokens (not cookie-based)
      if (newTokens.accessToken) {
        setMiddlewareTokens(newTokens.accessToken, newTokens.refreshToken, newTokens.expiresIn);
      }

      // Set tenant ID if organization is provided
      if (org) {
        setMiddlewareTenantId(org.id);
      }

      // 3. Persist to storage
      // Store tokens for both cookie-based and token-based auth:
      // - Token-based: Primary storage for auth state
      // - Cookie-based: Fallback for environments where cookies don't persist (e.g., jsdom tests)
      await Promise.all([
        storage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, newTokens.accessToken),
        storage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, newTokens.refreshToken),
        storage.setItem(AUTH_STORAGE_KEYS.USER, JSON.stringify(newUser)),
        org ? storage.setItem(AUTH_STORAGE_KEYS.CURRENT_TENANT, JSON.stringify(org)) : null,
      ]);
    },
    []
  );

  /**
   * Clear all authentication state (tokens, user, storage, middleware)
   */
  const clearAllAuthState = useCallback(async () => {
    const storage = getTokenStorage();

    // 1. Clear React state
    setTokensState(null);
    setUser(null);
    setCurrentOrganizationState(null);
    setOrganizations([]);

    // 2. Clear api-client middleware
    clearMiddlewareTokens();
    setMiddlewareTenantId(null);

    // 3. Clear storage — use allSettled so one failure doesn't block clearing other keys
    await Promise.allSettled([
      storage.removeItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN),
      storage.removeItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN),
      storage.removeItem(AUTH_STORAGE_KEYS.USER),
      storage.removeItem(AUTH_STORAGE_KEYS.CURRENT_TENANT),
    ]);

    // 4. Clear better-auth browser cookies (non-HttpOnly session_data)
    if (typeof document !== 'undefined') {
      const cookieNames = ['better-auth.session_data'];
      for (let i = 0; i <= 9; i++) {
        cookieNames.push(`better-auth.session_data.${i}`);
      }
      for (const name of cookieNames) {
        document.cookie = `${name}=; Max-Age=0; path=/`;
      }
    }
  }, []);

  /**
   * Hydrate auth state on mount
   * For local auth: reads from storage
   * For Better Auth: checks session via cookies
   */
  const hydrateAuth = useCallback(async () => {
    if (hydrationAttempted.current) return;
    hydrationAttempted.current = true;

    const storage = getTokenStorage();

    // Check if a stored token is an expired JWT. Non-JWT tokens (e.g., in tests) pass through.
    const isStoredJwtExpired = (token: string): boolean => {
      const isJwtFormat = token.split('.').length === 3;
      return isJwtFormat && isTokenExpired(token);
    };

    // Restore auth state from stored values (shared by cookie-fallback and token-based paths)
    const restoreFromStorage = (
      accessToken: string,
      refreshToken: string | null,
      userJson: string,
      orgJson: string | null
    ) => {
      const storedUser = JSON.parse(userJson) as User;

      const restoredTokens: AuthTokens = {
        accessToken,
        refreshToken: refreshToken || '',
        expiresIn: DEFAULT_SESSION_EXPIRES_IN,
        expiresAt: Date.now() + DEFAULT_SESSION_EXPIRES_IN * 1000,
      };

      setUser(storedUser);
      setTokensState(restoredTokens);
      setMiddlewareTokens(
        restoredTokens.accessToken,
        restoredTokens.refreshToken,
        restoredTokens.expiresIn
      );

      if (storedUser.organizations) {
        setOrganizations(storedUser.organizations);
      }

      if (orgJson) {
        try {
          const storedOrg = JSON.parse(orgJson) as Organization;
          setCurrentOrganizationState(storedOrg);
          setMiddlewareTenantId(storedOrg.id);
        } catch {
          // Ignore parse errors
        }
      } else if (storedUser.organizations && storedUser.organizations.length > 0) {
        setCurrentOrganizationState(storedUser.organizations[0]);
        setMiddlewareTenantId(storedUser.organizations[0].id);
      }
    };

    // Validate stored JWT and restore auth, with optional refresh on expiry
    const validateAndRestoreFromStorage = async (
      accessToken: string,
      refreshToken: string | null,
      userJson: string,
      orgJson: string | null,
      canRefresh: boolean
    ) => {
      if (!isStoredJwtExpired(accessToken)) {
        restoreFromStorage(accessToken, refreshToken, userJson, orgJson);
        return;
      }

      // Token expired — try refresh if available, otherwise force re-login
      if (canRefresh && refreshToken) {
        try {
          const refreshResult = await adapter.refreshSession(refreshToken);
          const storedUser = JSON.parse(userJson) as User;
          setUser(storedUser);
          setTokensState(refreshResult.tokens);
          setMiddlewareTokens(
            refreshResult.tokens.accessToken,
            refreshResult.tokens.refreshToken,
            refreshResult.tokens.expiresIn
          );
          if (refreshResult.tokens.refreshToken) {
            await storage.setItem(
              AUTH_STORAGE_KEYS.REFRESH_TOKEN,
              refreshResult.tokens.refreshToken
            );
          }
          return;
        } catch {
          // Refresh failed — fall through to clear
        }
      }

      await clearAllAuthState();
    };

    try {
      if (adapter.usesCookies) {
        // Cookie-based auth (Better Auth) - check session and extract JWT
        // Try stored refresh_token first (for environments where cookies don't persist like jsdom tests)
        // Fall back to cookie-based session if no stored token
        try {
          const [storedRefreshToken, storedUserJson] = await Promise.all([
            storage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN),
            storage.getItem(AUTH_STORAGE_KEYS.USER),
          ]);
          const result = await adapter.refreshSession(storedRefreshToken);

          // Use refreshed user if available, otherwise use stored user as fallback
          // (getSession may fail in jsdom where cookies don't persist)
          let hydratedUser = result.user;
          if ((!hydratedUser.id || !hydratedUser.email) && storedUserJson) {
            try {
              hydratedUser = JSON.parse(storedUserJson) as User;
            } catch {
              // Keep using result.user if parse fails
            }
          }

          // Valid session exists with extracted JWT
          setUser(hydratedUser);
          setTokensState(result.tokens);

          // Handle organizations (defensive check for adapters that may not return organizations)
          const resultOrgs = result.organizations || [];
          let orgs = resultOrgs.length > 0 ? resultOrgs : hydratedUser.organizations || [];

          // Sync JWT to api-client middleware for Authorization header
          if (result.tokens.accessToken) {
            setMiddlewareTokens(
              result.tokens.accessToken,
              result.tokens.refreshToken,
              result.tokens.expiresIn
            );
          }

          // Re-persist refreshed tokens (including rotated refresh token)
          if (result.tokens.refreshToken) {
            await storage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, result.tokens.refreshToken);
          }

          // Restore current organization from storage if available
          const orgJson = await storage.getItem(AUTH_STORAGE_KEYS.CURRENT_TENANT);
          let initialOrg: Organization | null = null;
          if (orgJson) {
            try {
              const storedOrg = JSON.parse(orgJson) as Organization;
              if (orgs.some((o: Organization) => o.id === storedOrg.id)) {
                initialOrg = storedOrg;
                setCurrentOrganizationState(storedOrg);
                setMiddlewareTenantId(storedOrg.id);
              } else if (orgs.length > 0) {
                initialOrg = orgs[0];
                setCurrentOrganizationState(orgs[0]);
                setMiddlewareTenantId(orgs[0].id);
              }
            } catch {
              if (orgs.length > 0) {
                initialOrg = orgs[0];
                setCurrentOrganizationState(orgs[0]);
                setMiddlewareTenantId(orgs[0].id);
              }
            }
          } else if (orgs.length > 0) {
            initialOrg = orgs[0];
            setCurrentOrganizationState(orgs[0]);
            setMiddlewareTenantId(orgs[0].id);
          }

          // Enrich org names if they are UUIDs (session refresh doesn't include org details)
          if (
            orgs.length > 0 &&
            orgs.some((o: Organization) => o.name === o.id) &&
            result.tokens.accessToken
          ) {
            try {
              const tenantId = initialOrg?.id || orgs[0].id;
              const response = await fetch(`${apiBaseUrl}/organizations/me`, {
                headers: {
                  Authorization: `Bearer ${result.tokens.accessToken}`,
                  'X-Tenant-ID': tenantId,
                },
              });
              if (response.ok) {
                const orgDetail = await response.json();
                orgs = orgs.map((o: Organization) =>
                  o.id === orgDetail.id ? { ...o, name: orgDetail.name } : o
                );
                if (initialOrg && initialOrg.id === orgDetail.id) {
                  const enrichedOrg = { ...initialOrg, name: orgDetail.name };
                  setCurrentOrganizationState(enrichedOrg);
                }
              }
            } catch {
              // Keep org ID as name if enrichment fails
            }
          }

          // Set organizations once with final (potentially enriched) value
          setOrganizations(orgs);
        } catch {
          // No valid Better Auth session - try localStorage fallback
          // This handles the case where tokens were set directly (e.g., accept-invite flow)
          // without going through Better Auth's session management
          const [accessToken, storedRefreshToken2, storedUserJson2, orgJson2] = await Promise.all([
            storage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN),
            storage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN),
            storage.getItem(AUTH_STORAGE_KEYS.USER),
            storage.getItem(AUTH_STORAGE_KEYS.CURRENT_TENANT),
          ]);

          if (accessToken && storedUserJson2) {
            try {
              await validateAndRestoreFromStorage(
                accessToken,
                storedRefreshToken2,
                storedUserJson2,
                orgJson2,
                true
              );
            } catch {
              // Failed to parse user from localStorage fallback
              // User remains unauthenticated
            }
          }
        }
      } else {
        // Token-based auth (local) - read from storage
        const [accessToken, refreshToken, userJson, orgJson] = await Promise.all([
          storage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN),
          storage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN),
          storage.getItem(AUTH_STORAGE_KEYS.USER),
          storage.getItem(AUTH_STORAGE_KEYS.CURRENT_TENANT),
        ]);

        if (accessToken && userJson) {
          try {
            await validateAndRestoreFromStorage(
              accessToken,
              refreshToken,
              userJson,
              orgJson,
              false
            );
          } catch {
            // Failed to parse user, clear auth state
            await clearAllAuthState();
          }
        }
      }
    } catch {
      // Hydration failed, start fresh
      console.warn('[AuthProvider] Hydration failed, starting with clean state');
    } finally {
      setIsLoading(false);
    }
  }, [adapter, appType, apiBaseUrl, clearAllAuthState]);

  // Run hydration on mount with timeout
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    let aborted = false;

    // Wrap hydration in Promise.race with timeout
    const hydrateWithTimeout = async () => {
      try {
        await Promise.race([
          hydrateAuth(),
          new Promise<void>(
            (_, reject) =>
              (timeoutId = setTimeout(() => {
                aborted = true;
                reject(new Error('Hydration timeout'));
              }, HYDRATION_TIMEOUT_MS))
          ),
        ]);
      } catch (error) {
        if (error instanceof Error && error.message === 'Hydration timeout') {
          // Timeout occurred — clear auth state and force login
          console.warn(`[AuthProvider] Hydration timeout after ${HYDRATION_TIMEOUT_MS}ms`);
          await clearAllAuthState();
          setIsLoading(false);
        } else {
          // Other hydration errors already handled in hydrateAuth
          throw error;
        }
      } finally {
        clearTimeout(timeoutId);
      }
    };

    hydrateWithTimeout()
      .then(() => {
        // If hydration completed but timeout already fired, the late-resolving
        // hydrateAuth may have set state after clearAllAuthState. Guard against this.
        if (aborted) {
          clearAllAuthState();
          setIsLoading(false);
        }
      })
      .catch((err: unknown) => {
        console.error('[AuthProvider] Hydration with timeout failed:', err);
      });

    return () => clearTimeout(timeoutId);
  }, [hydrateAuth, clearAllAuthState]);

  /**
   * Periodic session check for cookie-based auth
   * Validates session is still valid and refreshes if needed
   */
  useEffect(() => {
    if (!adapter.usesCookies || !isAuthenticated) return;

    const checkSession = async () => {
      try {
        const session = await adapter.getSession();
        if (!session) {
          // Session expired, clear state
          await clearAllAuthState();
        }
      } catch {
        // Session check failed, but don't logout - might be network issue
        console.warn('[AuthProvider] Session check failed');
      }
    };

    // Check session every 5 minutes
    const interval = setInterval(checkSession, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [adapter, isAuthenticated, clearAllAuthState]);

  /**
   * Clear any auth errors
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Login with credentials
   * Uses the configured adapter (local or better-auth)
   */
  const login = useCallback(
    async (credentials: LoginCredentials) => {
      setIsLoading(true);
      setError(null);

      try {
        const result = await adapter.login(credentials);
        // Update organizations (defensive check for adapters that may not return organizations)
        const resultOrgs = result.organizations || [];
        if (resultOrgs.length > 0) {
          setOrganizations(resultOrgs);
        }

        // Determine initial organization
        let initialOrg: Organization | null = null;
        if (resultOrgs.length > 0) {
          initialOrg = resultOrgs[0];
        } else if (result.user.organizations && result.user.organizations.length > 0) {
          setOrganizations(result.user.organizations);
          initialOrg = result.user.organizations[0];
        }

        // Persist and sync
        await persistAndSyncTokens(result.tokens, result.user, initialOrg);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Login failed';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [adapter, persistAndSyncTokens]
  );

  /**
   * Logout and clear all auth state
   */
  const logout = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Notify server (adapter handles this)
      await adapter.logout(tokens?.accessToken || null);
    } finally {
      await clearAllAuthState();
      setIsLoading(false);
    }
  }, [adapter, tokens, clearAllAuthState]);

  /**
   * Immediately invalidate session without server logout.
   * Used by auth middleware on 401 when the server has already rejected the session.
   * Unlike logout(), this skips the server-side logout call.
   */
  const triggerSessionInvalidation = useCallback(async () => {
    setIsSessionInvalidating(true);
    await clearAllAuthState();
  }, [clearAllAuthState]);

  // Reset invalidation flag once user state clears
  useEffect(() => {
    if (!user && isSessionInvalidating) {
      setIsSessionInvalidating(false);
    }
  }, [user, isSessionInvalidating]);

  /**
   * Refresh the access token
   * Uses the configured adapter
   */
  const refreshToken = useCallback(async () => {
    try {
      const result = await adapter.refreshSession(tokens?.refreshToken || null);

      // Merge with existing user if refresh didn't return full user
      const refreshedUser = result.user.id && result.user.email ? result.user : user || result.user;

      // Update tokens while keeping current user and organization
      if (refreshedUser) {
        await persistAndSyncTokens(result.tokens, refreshedUser, currentOrganization);
      } else {
        // Just update tokens state
        setTokensState(result.tokens);
        if (result.tokens.accessToken) {
          setMiddlewareTokens(
            result.tokens.accessToken,
            result.tokens.refreshToken,
            result.tokens.expiresIn
          );
        }
      }
    } catch (err) {
      // Do NOT clear auth state here. The existing JWT remains valid until it expires,
      // and the api-client middleware's 401 handler will sign the user out when needed.
      // Clearing here causes false logouts when the refresh token was rotated by the
      // middleware (race condition) or on transient network errors.
      throw err;
    }
  }, [adapter, tokens, user, currentOrganization, persistAndSyncTokens]);

  /**
   * Switch to a different organization (multi-tenant)
   */
  const switchOrganization = useCallback(
    async (organizationId: string) => {
      const org = organizations.find((o) => o.id === organizationId);
      if (!org) {
        throw new Error(`Organization ${organizationId} not found`);
      }

      // Sync to api-client middleware FIRST so getTenantId() returns the new
      // value on any re-render triggered by the state update below.
      setMiddlewareTenantId(org.id);

      // Update state (triggers re-render; getTenantId() already returns new org)
      setCurrentOrganizationState(org);

      // Persist to storage
      const storage = getTokenStorage();
      await storage.setItem(AUTH_STORAGE_KEYS.CURRENT_TENANT, JSON.stringify(org));
    },
    [organizations]
  );

  /**
   * Check if user has a specific permission
   */
  const hasPermission = useCallback(
    (permission: keyof UserPermissions): boolean => {
      if (!user?.permissions) return false;
      return user.permissions[permission] === true;
    },
    [user]
  );

  /**
   * Check if user has a specific role
   */
  const hasRole = useCallback(
    (role: UserRole | UserRole[]): boolean => {
      if (!user?.role) return false;
      if (Array.isArray(role)) {
        return role.includes(user.role);
      }
      return user.role === role;
    },
    [user]
  );

  /**
   * Get the current access token (for manual API calls)
   * Returns null for cookie-based auth
   */
  const getAccessToken = useCallback(() => {
    return tokens?.accessToken ?? null;
  }, [tokens]);

  /**
   * Set auth state from a login response (for accept-invite, external auth flows)
   * This allows setting tokens and user from external API responses without going through login()
   */
  const setAuthFromLoginResponse = useCallback(
    async (response: LoginResponse) => {
      setIsLoading(true);
      setError(null);

      try {
        const newTokens: AuthTokens = {
          accessToken: response.token,
          refreshToken: response.refresh_token,
          expiresIn: response.expires_in || DEFAULT_SESSION_EXPIRES_IN,
          expiresAt: Date.now() + (response.expires_in || DEFAULT_SESSION_EXPIRES_IN) * 1000,
        };

        // Normalize the user profile
        const normalizedUser = normalizeProfile(
          response.user as unknown as Record<string, unknown>,
          appType
        );

        // Determine initial organization
        let initialOrg: Organization | null = null;
        if (normalizedUser.organizations && normalizedUser.organizations.length > 0) {
          setOrganizations(normalizedUser.organizations);
          initialOrg = normalizedUser.organizations[0];
        }

        // Persist and sync
        await persistAndSyncTokens(newTokens, normalizedUser, initialOrg);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to set auth state';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [appType, persistAndSyncTokens]
  );

  const contextValue: AuthContextValue = useMemo(
    () => ({
      // State
      user,
      tokens,
      isAuthenticated,
      isLoading,
      error,
      currentOrganization,
      organizations,

      // Actions
      login,
      logout,
      refreshToken,
      switchOrganization,
      hasPermission,
      hasRole,
      getAccessToken,
      clearError,
      setAuthFromLoginResponse,
      triggerSessionInvalidation,
      isSessionInvalidating,
    }),
    [
      user,
      tokens,
      isAuthenticated,
      isLoading,
      error,
      currentOrganization,
      organizations,
      login,
      logout,
      refreshToken,
      switchOrganization,
      hasPermission,
      hasRole,
      getAccessToken,
      clearError,
      setAuthFromLoginResponse,
      triggerSessionInvalidation,
      isSessionInvalidating,
    ]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access authentication context
 *
 * @throws Error if used outside of AuthProvider
 *
 * @example
 * ```tsx
 * function LoginButton() {
 *   const { login, isLoading, error } = useAuth();
 *
 *   const handleLogin = async () => {
 *     await login({ email: 'user@example.com', password: 'password' });
 *   };
 *
 *   return (
 *     <button onClick={handleLogin} disabled={isLoading}>
 *       {isLoading ? 'Logging in...' : 'Login'}
 *     </button>
 *   );
 * }
 * ```
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
